import { fechaApiDate, ahoraProduccion, formatearFechaApi } from '../lib/format';
/**
 * dashboard.tsx — Home / lanzador de módulos (paridad con prod
 * puntodeventa.online/sistema/modulos): barra superior blanca (logo + tenant +
 * usuario), tarjeta "Resumen Hoy" con KPIs de ventas, y grids de tarjetas de
 * módulos agrupadas en PUNTO DE VENTA y ADMINISTRACIÓN. Sin sidebar.
 *
 * Reemplaza el dashboard antiguo (sidebar Layout + KPIs/charts) por el lanzador
 * moderno de prod. Conserva el fetch a GET /api/dashboard para el Resumen Hoy.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBranchSession } from '../hooks/useBranchSession';
import { SucursalSelectorModal } from '../components/layout/SucursalSelectorModal';
import type { DashboardData } from '@netcom/shared';

const money = (n: number) =>
  `$${(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Íconos inline (SVG; el home no va bajo .pos-legacy) ──────────────────────
const ic = (p: ReactNode) => ({ className = 'w-6 h-6' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);
const IcCart = ic(<><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></>);
const IcList = ic(<><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></>);
const IcGear = ic(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>);
const IcDoc = ic(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M16 13H8M16 17H8" /></>);
const IcChartBar = ic(<><path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="7" /><rect x="13" y="6" width="3" height="11" /></>);
const IcClipboard = ic(<><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 14l2 2 4-4" /></>);
const IcUsers = ic(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></>);
const IcInvoice = ic(<><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1Z" /><path d="M8 7h8M8 11h6M8 15h4" /></>);
const IcAnalytics = ic(<><path d="M3 3v18h18" /><path d="m7 14 3-3 3 3 5-6" /></>);
const IcBag = ic(<><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18M16 10a4 4 0 0 1-8 0" /></>);
const IcBox = ic(<><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5M12 22V12" /></>);
const IcExpand = ic(<><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3m13-5v3a2 2 0 0 1-2 2h-3" /></>);
const IcChevR = ic(<path d="m9 18 6-6-6-6" />);

interface ModuleEntry { title: string; desc: string; to: string; Icon: (p: { className?: string }) => React.ReactElement }

const PUNTO_DE_VENTA: ModuleEntry[] = [
  { title: 'Ventas', desc: 'Registra ventas, reimprime y factura tickets', to: '/pos', Icon: IcCart },
  { title: 'Catálogos', desc: 'Gestiona productos, clientes y usuarios', to: '/catalogs/dashboard', Icon: IcList },
  { title: 'Configuración', desc: 'Ajusta opciones y formato de ticket', to: '/pos/settings/general', Icon: IcGear },
  { title: 'Cotizaciones', desc: 'Crea y consulta cotizaciones', to: '/quotations/dashboard', Icon: IcDoc },
  { title: 'Reportes', desc: 'Reportes y listados de ventas', to: '/reports', Icon: IcChartBar },
  { title: 'Inventario', desc: 'Administra y levanta inventarios', to: '/inventory/report', Icon: IcClipboard },
  { title: 'CRM', desc: 'Gestiona la relación con tus clientes', to: '/crm/dashboard', Icon: IcUsers },
];
const ADMINISTRACION: ModuleEntry[] = [
  { title: 'Facturación', desc: 'Emite CFDI, notas de crédito y REP', to: '/cfdi/dashboard', Icon: IcInvoice },
  { title: 'Informes Analíticos', desc: 'Inteligencia de negocio y reportes dinámicos', to: '/bi', Icon: IcAnalytics },
  { title: 'Adquisiciones', desc: 'Requisiciones, órdenes y notas de entrada', to: '/procurement', Icon: IcBag },
  { title: 'Inventario multisucursal', desc: 'Stock y transferencias entre sucursales', to: '/inventory', Icon: IcBox },
];

function ModuleCard({ entry, tone }: { entry: ModuleEntry; tone: 'blue' | 'green' }) {
  const iconBg = tone === 'green' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600';
  return (
    <Link to={entry.to} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition p-4 flex items-start gap-3">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}><entry.Icon className="w-6 h-6" /></div>
      <div className="min-w-0">
        <p className="font-bold text-slate-800 leading-tight">{entry.title}</p>
        <p className="text-sm text-slate-500 mt-0.5 leading-snug">{entry.desc}</p>
      </div>
    </Link>
  );
}

export default function DashboardPage(): React.ReactElement {
  const { user, logout } = useAuth();
  const { branchName, isMultiBranch, branchList, branchId, setBranchId } = useBranchSession();
  const [showSucursal, setShowSucursal] = useState(false);
  const navigate = useNavigate();
  // logout() ya navega al login sin regreso (sesion-salida, como LogOff de prod); otra navegación aquí la pisaría.
  async function handleLogout(): Promise<void> {
    await logout();
  }
  const [menu, setMenu] = useState<string[]>([]);
  useEffect(() => { void apiGet<{ menu: string[] }>('/api/auth/session', { branchId }).then(r => setMenu(r.response?.menu ?? [])).catch(() => setMenu([])); }, [branchId]);
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    void apiGet<DashboardData>('/api/dashboard')
      .then((res) => setData(((res as { response?: DashboardData; data?: DashboardData }).response ?? (res as { data?: DashboardData }).data ?? (res as unknown as DashboardData)) || null))
      .catch(() => { /* el Resumen Hoy se muestra en ceros si falla */ });
  }, []);

  const sales = data?.sales;
  const cash = sales?.paymentBreakdown?.cash ?? 0;
  const card = (sales?.paymentBreakdown?.creditCard ?? 0) + (sales?.paymentBreakdown?.debitCard ?? 0);
  const total = sales?.totalToday ?? 0;
  const userName = (user as { name?: string; email?: string } | null)?.name || (user as { email?: string } | null)?.email || 'Usuario';
  const now = formatearFechaApi(ahoraProduccion(), { day: '2-digit', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Barra superior blanca (logo + tenant + usuario) */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="flex items-center h-16 px-6 gap-4">
          <img src="/kapi.png" alt="Kapi" className="h-8 w-auto" />
          <div className="flex-1 flex flex-col items-center justify-center">
            <p className="font-bold text-blue-600 leading-none">{branchName || 'DEMO'}</p>
            {isMultiBranch ? (
              <button
                type="button"
                onClick={() => setShowSucursal(true)}
                className="text-xs text-slate-400 hover:text-blue-600 transition-colors mt-0.5"
                title="Cambiar de sucursal"
              >
                Cambiar sucursal ▾
              </button>
            ) : (
              <p className="text-xs text-slate-400">Sucursal</p>
            )}
          </div>
          <button className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Pantalla completa"><IcExpand className="w-5 h-5" /></button>
          <div className="flex items-center gap-2">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-slate-700 leading-none">{userName}</p>
              <p className="text-xs text-slate-400">{(user as { email?: string } | null)?.email || ''}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold">{userName.charAt(0).toUpperCase()}</div>
            <button
              type="button"
              onClick={handleLogout}
              className="ml-1 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              title="Cerrar sesión"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 py-6 max-w-7xl mx-auto w-full">
        {/* Resumen Hoy */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-8">
          <div className="flex items-center flex-wrap gap-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Resumen hoy: <span className="text-slate-600 normal-case font-medium">{now}</span></p>
            <div className="flex items-center gap-6 ml-auto">
              {total <= 0 && <span className="text-blue-500 text-sm font-medium">Sin ventas</span>}
              <div className="bg-slate-50 rounded-lg px-4 py-2 text-center"><p className="text-lg font-bold text-slate-800">{money(cash)}</p><p className="text-xs text-slate-400 uppercase">Efectivo</p></div>
              <div className="bg-slate-50 rounded-lg px-4 py-2 text-center"><p className="text-lg font-bold text-slate-800">{money(card)}</p><p className="text-xs text-slate-400 uppercase">Tarjetas</p></div>
              <button onClick={() => navigate('/reports')} className="flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100">Ver resumen de ventas <IcChevR className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Punto de venta</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {PUNTO_DE_VENTA.filter(e => menu.includes(e.to)).map((m) => <ModuleCard key={m.to + m.title} entry={m} tone="blue" />)}
        </div>

        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Administración</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ADMINISTRACION.filter(e => menu.includes(e.to)).map((m) => <ModuleCard key={m.to + m.title} entry={m} tone="green" />)}
        </div>
      </main>

      <footer className="text-center text-xs text-slate-400 py-4">Copyright {fechaApiDate(ahoraProduccion()).getUTCFullYear()} © Kapitec Soluciones</footer>

      <SucursalSelectorModal
        open={showSucursal}
        mandatory={false}
        branches={branchList}
        currentId={branchId}
        onSelect={(id) => { setBranchId(id); setShowSucursal(false); }}
        onClose={() => setShowSucursal(false)}
      />
    </div>
  );
}
